import React, {ReactNode} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Divider from "@mui/joy/Divider";
import AspectRatio from '@mui/joy/AspectRatio';
import Alert from '@mui/material/Alert';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Tooltip from '@mui/joy/Tooltip';
import Checkbox from '@mui/joy/Checkbox';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Card from '@mui/joy/Card';
import CardActions from '@mui/joy/CardActions';
import CardOverflow from '@mui/joy/CardOverflow';
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import SensorWindowIcon from '@mui/icons-material/SensorWindow';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import {OsType} from "../../../models/osType";
import {proxiesSearchApi} from "../../proxies/proxiesApi";
import {NotificationService} from "../../../common/notification";
import {Proxy, ProxyDestination} from "../../../models/proxy";
import {Game} from "../../../models/games";
import {gamesListApi} from "../../games/gamesApi";
import CircularProgress from "@mui/joy/CircularProgress";
import {getBotApi, upsertBotApi} from "../botsApi";
import {BotGameInfo, BotMode, BotModeDescription, BotModeName, FullBotInfo} from "../../../models/bot";
import IconButton from "@mui/joy/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { formatDate } from '../../../common/dateTime';

export function BotGeneralInfo() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [proxies, setProxies] = React.useState<Proxy[]>([]);
    const [loadedProxies, setLoadedProxies] = React.useState(false);
    const [games, setGames] = React.useState<Game[]>([]);
    const [loadedGames, setLoadedGames] = React.useState(false);
    const [loadedBot, setLoadedBot] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    const [selectedProxy, setSelectedProxy] = React.useState<number>(-1);
    const [originalBotInfo, setOriginalBotInfo] = React.useState<FullBotInfo|undefined>(undefined);
    const [selectedOsType, setSelectedOsType] = React.useState<number>(20);
    const [selectedGames, setSelectedGames] = React.useState<BotGameInfo[]>([]);
    const [userName, setUserName] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [steamId, setSteamId] = React.useState<bigint>(BigInt(0));
    const [apiKey, setApiKey] = React.useState('');
    const [mobileAuthData, setMobileAuthData] = React.useState('');
    const [comment, setComment] = React.useState('');
    const [tradeLink, setTradeLink] = React.useState('');
    const [isDisabled, setIsDisabled] = React.useState(true);
    const [mode, setMode] = React.useState<BotMode>(0);
    const [level, setLevel] = React.useState<number>(0);
    const [bannedAt, setBannedAt] = React.useState<Date|undefined>(undefined);
    const [showModeHint, setShowModeHint] = React.useState(false);
    const [nickName, setNickName] = React.useState<string>('');
    const [avatar, setAvatar] = React.useState<string>('');

    React.useEffect(() => {
        loadProxy().catch(console.error);
        loadGames().catch(console.error);
        if (id && Number(id) > 0) {
            loadBot(Number(id)).catch(console.error);
        } else {
            setLoadedBot(true);
        }
    }, [])

    React.useEffect(() => {
        setShowModeHint(mode === 4 || mode === 5 || mode === 0);
    }, [mode])

    const loadBot = async (id: number) => {
        try {
            let result = await getBotApi(id);
            if (result.result) {
                setApiKey(result.value?.apiKey ?? '');
                setMobileAuthData(result.value?.mobileAuthData ?? '');
                setLevel(result.value?.level ?? 0);
                setBannedAt(result.value?.bannedAt);
                setComment(result.value?.comment ?? '');
                setTradeLink(result.value?.tradeLink ?? '');
                setUserName(result.value?.userName ?? '');
                setPassword(result.value?.password ?? '');
                setSelectedProxy(result.value?.proxyId ?? -1);
                setSelectedOsType(result.value?.osType ?? -1);
                setSelectedGames(result.value?.gamesInventoriesSize ?? []);
                setIsDisabled(result.value?.isDisabled ?? true);
                setMode(result.value?.mode ?? 0);
                setSteamId(result.value?.steamId ?? BigInt(0));
                setNickName(result.value?.nickName ?? '');
                setAvatar(result.value?.avatar ?? '');
                setOriginalBotInfo(result.value);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedBot(true);
        }
    }

    const loadProxy = async () => {
        try {
            //Беру заведомо большое число зная что прокси общего типа не будет много
            let result = await proxiesSearchApi({page:1,count:1000,destination: ProxyDestination.BotsOnly,linked:false});
            if (result.result) {
                setProxies(result.value!.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedProxies(true)
        }
    }

    const loadGames = async () => {
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedGames(true)
        }
    }

    const onCancel = () => {
        navigate('/app/bots');
    }

    const onSave = async () => {
        try {
            setIsSaving(true)
            const currentId = (id && Number(id) > 0) ? Number(id) : Number(0);
            let botGameInfos = selectedGames;
            if (mode === 0) {
                if (botGameInfos.length === 0) {
                    NotificationService.alertError("Для бота в режиме обработка оферов должна быть выбранна хотя бы одна игра.")
                    return
                }
            }
            if (mode === 4) {
                if (botGameInfos.length !== 1) {
                    NotificationService.alertError("Для бота в режиме обновления данных скина должна быть выбранна только одна игра.")
                    return
                }
            }
            if (mode === 5) {
                if (botGameInfos.length !== games.length) {
                    NotificationService.alertError("Для бота в режиме загрузки инвентаря необходимо выбрать все игры.")
                    return
                }
            }
            if (mode === 1 || mode === 6) {
                botGameInfos = [];
            }
            if (steamId <= 0) {
                NotificationService.alertError("Задайте Steam ID.")
                return
            }
            let result = await upsertBotApi({
                id: currentId,
                apiKey: apiKey,
                proxyId: selectedProxy > 0 ? selectedProxy : undefined,
                osType: selectedOsType,
                gamesInventoriesSize: botGameInfos,
                isDisabled: isDisabled,
                mobileAuthData: mobileAuthData,
                level: level,
                bannedAt: bannedAt,
                comment: comment,
                password: password,
                userName: userName,
                steamId: steamId,
                mode: mode
            });
            if (result.result) {
                navigate('/app/bots')
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setIsSaving(false)
        }
    }

    const copyTradeLink = async () => {
        if (!tradeLink) return;
        try {
            await navigator.clipboard.writeText(tradeLink);
            NotificationService.alertSuccess("Ссылка скопирована");
        } catch {
            NotificationService.alertError("Не удалось скопировать");
        }
    };

    const tradeLinkInput = (): ReactNode => (
        <Input size="sm" placeholder="Трейд ссылка" sx={{flexGrow: 1}}
               value={tradeLink}
               readOnly
               endDecorator={
                   <IconButton size="sm" onClick={copyTradeLink} disabled={!tradeLink}
                               aria-label="Копировать трейд ссылку">
                       <ContentCopyIcon/>
                   </IconButton>
               }/>
    );

    const gameRegion = (): ReactNode => {
        return mode === 1 || mode === 6 ? <></> :
            <Stack spacing={1}>
                <FormLabel>Игры</FormLabel>
                {selectedGames.map(game =>
                    <Stack direction={"row"} spacing={1} key={game.gameId}>
                        <Select
                            size="sm"
                            startDecorator={<VideogameAssetIcon/>}
                            value={game.gameId}
                            onChange={(_, value) => {
                                if(value) {
                                    setSelectedGames(prevGames => {
                                            return prevGames.map((g) =>
                                                g.gameId === game.gameId ? {...g, gameId: Number(value)} : g
                                            );
                                        }
                                    );
                                }
                            }}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        width: '100%',
                                    },
                                },
                            }}
                        >
                            {loadedGames ? <>
                                    {games.filter(g => g.id === game.gameId || !selectedGames.map(gm => gm.gameId).includes(g.id)).map((g) =>
                                        <Option key={g.id} value={g.id}>{g.name}</Option>
                                    )}
                                </> :
                                <CircularProgress/>
                            }
                        </Select>
                        <Input size="sm" placeholder="Кол-во скинов"
                               value={game.size}
                               onChange={(event) => {
                                   setSelectedGames(prevGames => {
                                           return prevGames.map((g) =>
                                               g.gameId === game.gameId ? {...g, size: Number(event.target.value)} : g
                                           );
                                       }
                                   );
                               }}/>
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            style={{width: '50px'}}
                            onClick={() => {
                                setSelectedGames(list => list.filter(l => l.gameId !== game.gameId));
                            }}
                        >
                            <RemoveIcon/>
                        </IconButton>
                    </Stack>
                )}
                {
                    selectedGames.length < games.length &&
                    <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        style={{width: '50px'}}
                        onClick={() => {
                            const existingGameIds = selectedGames.map(game => game.gameId);
                            const firstMissingGame = games.find(g => !existingGameIds.includes(g.id));
                            if (!firstMissingGame) {
                                NotificationService.alertError("Все игры добавлены");
                                return;
                            }
                            setSelectedGames(list => [...list, {gameId: firstMissingGame.id, size: 50}]);
                        }}
                    >
                        <AddIcon/>
                    </IconButton>
                }
            </Stack>
    }

    const renderModeHint = (): string | undefined => {
        switch(mode) {
            case 0: { 
                return "Для бота в режиме обмена через бота-посредника  необходимо выбрать хотя бы одну игру."
            }
            case 4: {
                return "Для бота в режиме обновления данных скина должна быть выбранна только одна игра. Игра должна быть обязательно добавлена в библиотеку в Steam."
            }
            case 5: { 
                return "Для бота в режиме загрузки инвентаря необходимо выбрать все игры."
            }
            default: {
                return undefined;
            }
        }
    }

    return (loadedBot
        ?<Card>
                <Box sx={{mb: 1}} style={bannedAt ? {backgroundColor: 'red'} : {}}>
                    <Typography level="title-md">{nickName ? nickName : 'Информация'} {bannedAt ? ` (бан бота от ${formatDate(bannedAt)})` : ''}</Typography>
                    <Typography level="body-sm">
                        Персональная информация бота (level #{level})
                    </Typography>
                </Box>
                <Divider/>
                <Stack
                    direction="row"
                    spacing={3}
                    sx={{display: {xs: 'none', md: 'flex'}, my: 1}}
                >
                    <Stack direction="column" spacing={1}>
                        <AspectRatio
                            ratio="1"
                            maxHeight={200}
                            sx={{flex: 1, minWidth: 120, borderRadius: '100%'}}
                        >
                            <img
                                src={avatar}
                                loading="lazy"
                                alt=""
                            />
                        </AspectRatio>
                    </Stack>
                    <Stack spacing={2} sx={{flexGrow: 1}}>
                        <Stack spacing={1}>
                            <FormLabel>Учетные данные</FormLabel>

                            <Input size="sm" placeholder="Steam ID"
                                    value={steamId.toString()}
                                    onChange={(event) => setSteamId(BigInt(event.target.value ?? 0))}/>
                            <Input size="sm" placeholder="Имя пользователя"
                                    value={userName}
                                    onChange={(event) => setUserName(event.target.value)}/>
                            <Input size="sm" placeholder="Пароль" sx={{flexGrow: 1}}
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}/>
                            <Input size="sm" placeholder="API ключ" sx={{flexGrow: 1}}
                                    value={apiKey} onChange={(event) => setApiKey(event.target.value)}/>
                            <Input size="sm" placeholder="Аутентификатор" sx={{flexGrow: 1}}
                                    value={mobileAuthData}
                                    onChange={(event) => setMobileAuthData(event.target.value)}/>
                            <Input size="sm" placeholder="Комментарий" sx={{flexGrow: 1}}
                                    value={comment}
                                    onChange={(event) => setComment(event.target.value)}/>
                            {tradeLinkInput()}
                            {gameRegion()}
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <FormLabel>Прокси</FormLabel>
                            <Select
                                size="sm"
                                startDecorator={<DeviceHubIcon/>}
                                defaultValue={-1}
                                value={selectedProxy}
                                onChange={(_, value) => setSelectedProxy(value ?? -1)}
                            >
                                {loadedProxies ? <>
                                        <Option key={-1} value={-1}>Без прокси</Option>
                                        {
                                            originalBotInfo?.proxyId
                                            ? <Option key={originalBotInfo.proxyId}
                                                        value={originalBotInfo.proxyId}>
                                                {originalBotInfo?.proxyAddress}
                                                </Option>
                                            : <></>
                                        }
                                        {proxies.map((p) =>
                                            <Option key={p.id}
                                                    value={p.id}>{p.proxyHost}:{p.proxyPort}</Option>
                                        )}
                                    </> :
                                    <CircularProgress/>
                                }
                            </Select>
                            <FormLabel>Тип OS</FormLabel>
                            <Select
                                size="sm"
                                startDecorator={<SensorWindowIcon/>}
                                value={selectedOsType}
                                onChange={(_, value) => setSelectedOsType(value ?? 20)}
                            >
                                {
                                    Object.entries(OsType).map((entity) => <Option key={entity[0]}
                                        value={Number(entity[1])}>{entity[0]}</Option>)
                                }
                            </Select>
                            <FormLabel>Режим работы</FormLabel>
                            <Select
                                size="sm"
                                startDecorator={<DeveloperModeIcon/>}
                                value={mode}
                                onChange={(_, value) => setMode(value as BotMode ?? 0)}
                            >
                                {
                                    Object.entries(BotModeName).map((entity) =>
                                        <Tooltip key={Number(entity[0])}
                                            title={BotModeDescription[(Number(entity[0]) as BotMode)]}>
                                            <Option value={Number(entity[0])}>{entity[1]}</Option>
                                        </Tooltip>
                                    )
                                }
                            </Select>
                        </Stack>
                        {showModeHint &&
                            <Alert variant="outlined" severity="info">{renderModeHint()}</Alert>
                        }
                        <Checkbox label="Активен"
                                    checked={!isDisabled}
                                    onChange={(event) => setIsDisabled(!event.target.checked)}/>
                    </Stack>
                </Stack>
                <Stack
                    direction="column"
                    spacing={2}
                    sx={{display: {xs: 'flex', md: 'none'}, my: 1}}
                >
                    <Stack direction="row" spacing={2}>
                        <Stack direction="column" spacing={1}>
                            <AspectRatio
                                ratio="1"
                                maxHeight={108}
                                sx={{flex: 1, minWidth: 108, borderRadius: '100%'}}
                            >
                                <img
                                    src={avatar}
                                    loading="lazy"
                                    alt=""
                                />
                            </AspectRatio>
                        </Stack>
                        <Stack spacing={1} sx={{flexGrow: 1}}>
                            <FormLabel>Учетные данные</FormLabel>
                            <Stack
                                sx={{
                                    display: {
                                        sm: 'flex-column',
                                        md: 'flex-row',
                                    },
                                    gap: 2,
                                }}
                            >
                                <Input size="sm" placeholder="Steam ID"
                                        value={steamId.toString()}
                                        onChange={(event) => setSteamId(BigInt(event.target.value ?? 0))}/>
                                <Input size="sm" placeholder="Имя пользователя"
                                        value={userName}
                                        onChange={(event) => setUserName(event.target.value)}/>
                                <Input size="sm" placeholder="Пароль"
                                        value={password}
                                        onChange={(event) => setPassword(event.target.value)}/>
                                <Input size="sm" placeholder="API ключ"
                                        value={apiKey} onChange={(event) => setApiKey(event.target.value)}/>
                                <Input size="sm" placeholder="Аутентификатор"
                                        value={mobileAuthData}
                                        onChange={(event) => setMobileAuthData(event.target.value)}/>
                                <Input size="sm" placeholder="Комментарий"
                                        value={comment}
                                        onChange={(event) => setComment(event.target.value)}/>
                                {tradeLinkInput()}
                            </Stack>
                        </Stack>
                    </Stack>
                    {gameRegion()}
                    <FormControl>
                        <FormLabel>Прокси</FormLabel>
                        <Select
                            size="sm"
                            startDecorator={<DeviceHubIcon/>}
                            defaultValue={-1}
                            value={selectedProxy}
                            onChange={(_, value) => setSelectedProxy(value ?? -1)}
                        >
                            {loadedProxies ? <>
                                    <Option key={-1} value={-1}>Без прокси</Option>
                                    {
                                        originalBotInfo?.proxyId
                                        ? <Option key={originalBotInfo.proxyId}
                                                    value={originalBotInfo.proxyId}>
                                            {originalBotInfo?.proxyAddress}
                                            </Option>
                                        : <></>
                                    }
                                    {proxies.map((p) =>
                                        <Option key={p.id} value={p.id}>{p.proxyHost}:{p.proxyPort}</Option>
                                    )}
                                </> :
                                <CircularProgress/>
                            }
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Тип OS</FormLabel>
                        <Select
                            size="sm"
                            startDecorator={<SensorWindowIcon/>}
                            value={selectedOsType}
                            onChange={(_, value) => setSelectedOsType(value ?? 20)}
                        >
                            {
                                Object.entries(OsType).map((entity) => <Option key={entity[0]}
                                    value={Number(entity[1])}>{entity[0]}</Option>)
                            }
                        </Select>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Режим работы</FormLabel>
                        <Select
                            size="sm"
                            startDecorator={<DeveloperModeIcon/>}
                            value={mode}
                            onChange={(_, value) => setMode(value as BotMode ?? 0)}
                        >
                            {
                                Object.entries(BotModeName).map((entity) =>
                                    <Tooltip title={BotModeDescription[(Number(entity[0]) as BotMode)]} key={Number(entity[0])}>
                                        <Option value={Number(entity[0])}>{entity[1]}</Option>
                                    </Tooltip>
                                )
                            }
                        </Select>
                    </FormControl>
                    <Checkbox label="Активен"
                                checked={!isDisabled}
                                onChange={(event) => setIsDisabled(!event.target.checked)}/>
                    {showModeHint &&
                        <Alert variant="outlined" severity="info">{renderModeHint()}</Alert>
                    }
                </Stack>
                <CardOverflow sx={{borderTop: '1px solid', borderColor: 'divider'}}>
                    <CardActions sx={{alignSelf: 'flex-end', pt: 2}}>
                        <Button size="sm" variant="outlined" color="neutral" onClick={onCancel}
                                disabled={isSaving}>
                            Отмена
                        </Button>
                        <Button size="sm" variant="solid" onClick={async () => await onSave()}
                                disabled={isSaving}>
                            Сохранить
                        </Button>
                    </CardActions>
                </CardOverflow>
        </Card>
        : <CircularProgress/>);
}
